import type { DeclarationOfConformity, UdiEudamedData } from '@/features/dossier/api';
import { IvdrTdRenderer } from './IvdrTdRenderer';
import { IvdrPmsRenderer } from './IvdrPmsRenderer';
import { IvdrSspRenderer } from './IvdrSspRenderer';
import {
  IvdrConformityRouteRenderer,
  type IvdrConformityResult,
} from './IvdrConformityRouteRenderer';
import {
  IvdrPerformanceEvalRenderer,
  type PerfEvalResult,
} from './IvdrPerformanceEvalRenderer';
import { DeclarationOfConformityRenderer } from './DeclarationOfConformityPanel';
import { UdiEudamedDataRenderer } from './UdiEudamedDataPanel';
import { PsurRenderer } from './PsurRenderer';
import { ReferencesRenderer } from './ReferencesRenderer';
import {
  appliesToAllClasses,
  pickDossier,
  pickSubmissionHeader,
  type EuDossierProfile,
} from './euDossierProfile';

/**
 * EU IVDR (Regulation (EU) 2017/746) dossier profile — the on-screen mirror of
 * the backend `eu_ivdr_combined_renderer`. Owns ONLY IVDR Parts, titles, article
 * numbers, and applicability; it is wholly independent of the MDR profile. IVDs
 * are not implanted, so there is no implant card / SSCP lay summary here.
 *
 * Document order (Part 1..N):
 *   td → pms (Art 79) → SSP (Art 29, Class C / D only) →
 *   conformity (Arts 48-50) → performance evaluation (Art 56 / Annex XIII) →
 *   DoC → UDI.
 * Appendices: PSUR (Art 81) → references.
 */

/** The SSP (Article 29) is required only for Class C and D. */
const isClassCorD = (deviceClass: string): boolean =>
  deviceClass === 'C' || deviceClass === 'D';

export const ivdrDossierProfile: EuDossierProfile = {
  regulation: 'IVDR',
  numberedParts: [
    {
      id: 'eu-sec-td',
      navTitle: 'Technical Documentation',
      title: 'Annex II Technical Documentation',
      applicable: appliesToAllClasses,
      pick: (p) => p.td,
      render: (part) => (
        <IvdrTdRenderer
          dossier={pickDossier(part) ?? {}}
          submissionHeader={pickSubmissionHeader(part) ?? undefined}
          hideCover
        />
      ),
    },
    {
      id: 'eu-sec-pms',
      navTitle: 'Post-Market Surveillance Plan',
      title: 'Annex III Post-Market Surveillance Plan (Article 79)',
      applicable: appliesToAllClasses,
      pick: (p) => p.pms,
      render: (part) => <IvdrPmsRenderer dossier={pickDossier(part) ?? {}} />,
    },
    {
      id: 'eu-sec-ssp',
      navTitle: 'Summary of Safety and Performance',
      title: 'Summary of Safety and Performance (Article 29)',
      applicable: isClassCorD,
      pick: (p) => p.ssp ?? null,
      render: (part) => <IvdrSspRenderer dossier={pickDossier(part) ?? {}} />,
    },
    {
      id: 'eu-sec-conformity_route',
      navTitle: 'Conformity Route',
      title: 'Conformity Assessment Route (Articles 48-50)',
      applicable: appliesToAllClasses,
      pick: (p) => p.conformity_route,
      render: (part) => (
        <IvdrConformityRouteRenderer result={part as IvdrConformityResult} />
      ),
    },
    {
      id: 'eu-sec-performance_evaluation',
      navTitle: 'Performance Evaluation',
      title: 'Performance Evaluation (Article 56 / Annex XIII)',
      applicable: appliesToAllClasses,
      pick: (p) => p.performance_evaluation ?? null,
      render: (part) => <IvdrPerformanceEvalRenderer result={part as PerfEvalResult} />,
    },
    {
      id: 'eu-sec-declaration_of_conformity',
      navTitle: 'Declaration of Conformity',
      title: 'Declaration of Conformity (Annex IV)',
      applicable: appliesToAllClasses,
      pick: (p) => p.declaration_of_conformity,
      render: (part, ctx) => (
        <DeclarationOfConformityRenderer
          result={part as DeclarationOfConformity}
          productId={ctx.productId}
          deviceClass={ctx.deviceClass}
          regulation="IVDR"
          hideDownload
        />
      ),
    },
    {
      id: 'eu-sec-udi_eudamed_data',
      navTitle: 'UDI / EUDAMED',
      title: 'UDI / EUDAMED Data (Annex VI)',
      applicable: appliesToAllClasses,
      pick: (p) => p.udi_eudamed_data,
      render: (part, ctx) => (
        <UdiEudamedDataRenderer
          result={part as UdiEudamedData}
          productId={ctx.productId}
          deviceClass={ctx.deviceClass}
          regulation="IVDR"
          hideDownload
        />
      ),
    },
  ],
  appendices: [
    {
      id: 'eu-sec-psur',
      navTitle: 'PSUR (Article 81)',
      title: 'Periodic Safety Update Report (Article 81)',
      applicable: appliesToAllClasses,
      pick: (p) => p.psur ?? null,
      render: (part) => <PsurRenderer psur={part as Record<string, unknown>} />,
    },
    {
      id: 'eu-sec-references',
      navTitle: 'References',
      title: 'References',
      applicable: appliesToAllClasses,
      pick: (p) => (p.references && p.references.length > 0 ? p.references : null),
      render: (part) => <ReferencesRenderer references={part as string[]} />,
    },
  ],
};
